import { Link, useNavigate } from "react-router-dom";
import { LogOut, ArrowLeft, BookOpen, LineChart, Sun, Moon, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";

interface AppHeaderProps {
  /** When true, hides the auth-related buttons (used on /auth itself) */
  minimal?: boolean;
  /** When provided, shows a Back link instead of the New Session button area */
  backTo?: string;
}

export function AppHeader({ minimal = false, backTo }: AppHeaderProps) {
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out");
    navigate("/");
  };

  return (
    <header className="border-b border-border/40 backdrop-blur-sm sticky top-0 z-50 bg-background/60">
      <div className="container flex h-16 items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2">
          <BrandLogo className="h-8 w-8" />
          <span className="font-display font-bold text-lg">SpeakLemon</span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          {minimal ? null : user ? (
            <>
              {backTo ? (
                <Button asChild variant="ghost" size="sm">
                  <Link to={backTo}><ArrowLeft className="mr-1 h-4 w-4" /> Back</Link>
                </Button>
              ) : (
                <Button asChild variant="ghost" size="sm">
                  <Link to="/session">New Session</Link>
                </Button>
              )}
              <Button asChild variant="ghost" size="sm">
                <Link to="/prepare"><BookOpen className="mr-1 h-4 w-4" /> Prep library</Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/progress"><LineChart className="mr-1 h-4 w-4" /> My progress</Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/blog"><Newspaper className="mr-1 h-4 w-4" /> IELTS News & Advice</Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/pricing">Pricing</Link>
              </Button>
              <Button variant="outline" size="sm" onClick={handleSignOut}>
                <LogOut className="mr-1 h-4 w-4" /> Log out
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm">
                <Link to="/prepare">Prep library</Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/blog">IELTS News & Advice</Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/pricing">Pricing</Link>
              </Button>
              <Button asChild variant="ghost" size="sm"><Link to="/auth">Sign in</Link></Button>
              <Button asChild size="sm"><Link to="/auth">Get started</Link></Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}